export type SourceType =
  | 'official'
  | 'public_portal'
  | 'marketing'
  | 'public_record_aggregator';

export type CitationRef = {
  sourceId: string;
  label?: string;
  context?: string;
};

export type SourceRecord = {
  id: string;
  title: string;
  domain: string;
  url: string;
  observedAt: string;
  sourceType: SourceType;
  shortLabel?: string;
  drawerSummary?: string;
  note?: string;
};

export type RecommendationScenario = {
  id: string;
  label: string;
  askRent: string;
  effectiveRent: string;
  confidence: string;
  posture: string;
  rationale: string;
  triggers: string[];
  sourceIds: string[];
};

export type BuildingFact = {
  field: string;
  chosenValue: string;
  confidence: string;
  contradiction: string;
  sourceIds: string[];
};

export type TimelineEvent = {
  date: string;
  title: string;
  details: string;
  sourceIds: string[];
};

export type UnitEvent = {
  date: string;
  price: string;
  eventType: string;
  portals: string[];
  notes: string;
  sourceIds: string[];
};

export type UnitRecord = {
  id: string;
  label: string;
  bedrooms: string;
  bathrooms: string;
  status: string;
  traceStatus: string;
  baselineNotes: string;
  inferredFeatures: string[];
  ledger: UnitEvent[];
  supportingEvidence: {
    summary: string;
    sourceIds: string[];
  }[];
  portalsSearched?: string[];
};

export type CompLifecycleStatus =
  | 'active'
  | 'price_cut'
  | 'removed'
  | 'off_market'
  | 'leased_inferred'
  | 'leased_confirmed'
  | 'expired'
  | 'relisted';

export type CompRole = 'primary' | 'secondary' | 'ceiling' | 'calibration';

export type CompOutcomeStatus =
  | 'active'
  | 'off_market_inferred'
  | 'leased_confirmed'
  | 'historical';

export type CompLifecycleEvent = {
  date: string;
  status: CompLifecycleStatus;
  ask?: string;
  note: string;
  sourceIds: string[];
};

export type ComparableRecord = {
  id: string;
  label: string;
  address: string;
  bedBath: string;
  sqft: string;
  observedAt: string;
  role: CompRole;
  fit: string;
  features: string;
  adjustment: string;
  currentAsk?: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
  daysVisible?: string;
  outcomeStatus: CompOutcomeStatus;
  outcomeConfidence: 'high' | 'moderate' | 'low';
  outcomeLabel: string;
  lifecycle: CompLifecycleEvent[];
  citationRefs?: CitationRef[];
  sourceIds: string[];
};

export type CompRecord = {
  address: string;
  ask: string;
  bedBath: string;
  sqft: string;
  observed: string;
  fit: string;
  features: string;
  adjustment: string;
  sourceIds: string[];
};

export type HistoricalComp = {
  name: string;
  observedFacts: string;
  timing: string;
  readThrough: string;
  sourceIds: string[];
};

export type ContradictionRecord = {
  field: string;
  values: string;
  resolution: string;
  severity: 'Low' | 'Medium' | 'High';
  sourceIds: string[];
};

export type ConstraintRecord = {
  title: string;
  takeaway: string;
  applicability: string;
  sourceIds: string[];
};

export type EvidenceRecord = {
  id: string;
  subject: string;
  category: string;
  claim: string;
  sourceIds: string[];
};

export const lastRefresh = 'April 22, 2026';

export const sources: SourceRecord[] = [
  {
    id: 'S1',
    title: 'Zillow Unit 1',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Roque-Moraes-Ct-1-Mill-Valley-CA-94941/446203596_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
    drawerSummary:
      'Exact Unit 1 page with the January and March 2025 pricing sequence.',
  },
  {
    id: 'S2',
    title: 'Trulia Unit 1',
    domain: 'trulia.com',
    url: 'https://www.trulia.com/home/17-roque-moraes-ct-1-mill-valley-ca-94941-446203596',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
    drawerSummary: 'Trulia mirror of the Unit 1 rental history.',
  },
  {
    id: 'S3',
    title: 'HotPads Unit 1',
    domain: 'hotpads.com',
    url: 'https://hotpads.com/17-roque-moraes-ct-mill-valley-ca-94941-1m8by5w/1/pad',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
    drawerSummary:
      'HotPads preserve of the Unit 1 $2,575 ask and off-market date.',
  },
  {
    id: 'S4',
    title: 'Zillow Unit 2',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Roque-Moraes-Ct-APT-2-Mill-Valley-CA-94941/447478865_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S5',
    title: 'Trulia Unit 2',
    domain: 'trulia.com',
    url: 'https://www.trulia.com/home/17-roque-moraes-ct-2-mill-valley-ca-94941-447478865',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S6',
    title: 'HotPads Unit 2',
    domain: 'hotpads.com',
    url: 'https://hotpads.com/17-roque-moraes-ct-mill-valley-ca-94941-1m8by5w/2/pad',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S7',
    title: 'Zillow Unit 4',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Roque-Moraes-Ct-4-Mill-Valley-CA-94941/442305156_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S8',
    title: 'HotPads Unit 4',
    domain: 'hotpads.com',
    url: 'https://hotpads.com/17-roque-moraes-ct-mill-valley-ca-94941-1m8by5w/4/pad',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S9',
    title: 'Zillow Building',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Roque-Moraes-Ct-Mill-Valley-CA-94941/80748528_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S10',
    title: 'Redfin Building',
    domain: 'redfin.com',
    url: 'https://www.redfin.com/CA/Mill-Valley/17-Roque-Moraes-Ct-94941/home/587820',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S11',
    title: 'Compass Closed Listing',
    domain: 'compass.com',
    url: 'https://www.compass.com/homedetails/17-Roque-Moraes-Ct-Mill-Valley-CA-94941/1OXW51_pid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S12',
    title: 'LoopNet Building Listing',
    domain: 'loopnet.com',
    url: 'https://www.loopnet.com/Listing/17-Roque-Moraes-Ct-Mill-Valley-CA/37861860/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S13',
    title: 'Apartments.com Building Page',
    domain: 'apartments.com',
    url: 'https://www.apartments.com/17-roque-moraes-ct-mill-valley-ca/m4ghzkk/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S14',
    title: 'Realtor Building',
    domain: 'realtor.com',
    url: 'https://www.realtor.com/realestateandhomes-detail/17-Roque-Moraes-Ct_Mill-Valley_CA_94941_M28657-23238',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S15',
    title: 'Homes.com Building',
    domain: 'homes.com',
    url: 'https://www.homes.com/property/17-roque-moraes-ct-mill-valley-ca/ppp7yxfx16tfd/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S16',
    title: 'Crexi Property Record',
    domain: 'crexi.com',
    url: 'https://www.crexi.com/property-records/030-191-18-MILL-VALLEY-CA-94941/660fa252f6e276402918d585e40325e381d2651b',
    observedAt: '2026-04-22',
    sourceType: 'public_record_aggregator',
  },
  {
    id: 'S17',
    title: '17 Roque Moraes Microsite',
    domain: '17roquemoraes.com',
    url: 'https://www.17roquemoraes.com/',
    observedAt: '2026-04-22',
    sourceType: 'marketing',
  },
  {
    id: 'S18',
    title: '17 Shelley Dr Apt 4',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Shelley-Dr-APT-4-Mill-Valley-CA-94941/2120874785_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S19',
    title: '1 Shelley Dr #C',
    domain: 'hotpads.com',
    url: 'https://hotpads.com/1-shelley-dr-mill-valley-ca-94941-1m8bygk/c/pad',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S20',
    title: '1030 Redwood Hwy #2',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/apartments/mill-valley-ca/1030-redwood-highway/9NWTBw/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S21',
    title: 'Trailhead at Tam Junction',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/apartments/mill-valley-ca/trailhead-apartments-at-tam-junction/CkCRdX/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S22',
    title: '37 Reed Blvd #9D',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/37-Reed-Blvd-9D-Mill-Valley-CA-94941/461658869_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S23',
    title: 'Chateau Chillon',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/apartments/mill-valley-ca/chateau-chillon/5XjKJ4/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S24',
    title: '25 Roque Moraes Ct Apt 6',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/25-Roque-Moraes-Ct-APT-6-Mill-Valley-CA-94941/366580201_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S25',
    title: '251 Miller Ave Apt 2',
    domain: 'trulia.com',
    url: 'https://www.trulia.com/home/251-miller-ave-2-mill-valley-ca-94941-2110335209',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S26',
    title: 'California Civil Code 1947.12',
    domain: 'leginfo.legislature.ca.gov',
    url: 'https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CIV&sectionNum=1947.12.',
    observedAt: '2026-04-22',
    sourceType: 'official',
  },
  {
    id: 'S27',
    title: 'California Civil Code 1946.2',
    domain: 'leginfo.legislature.ca.gov',
    url: 'https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=CIV&sectionNum=1946.2.',
    observedAt: '2026-04-22',
    sourceType: 'official',
  },
  {
    id: 'S28',
    title: 'Mill Valley Housing Policy Materials',
    domain: 'cityofmillvalley.gov',
    url: 'https://www.cityofmillvalley.gov/DocumentCenter/View/2955/Workshop-4-Materials---Policies-and-Programs-003?bidId=',
    observedAt: '2026-04-22',
    sourceType: 'official',
  },
  {
    id: 'S29',
    title: 'Marin County Multi-Unit Rental Housing Inspections',
    domain: 'marincounty.gov',
    url: 'https://www.marincounty.gov/departments/cda/env-health-svcs/prgm-rental-inspection',
    observedAt: '2026-04-22',
    sourceType: 'official',
  },
  {
    id: 'S30',
    title: 'Mill Valley Lease to Locals',
    domain: 'cityofmillvalley.gov',
    url: 'https://www.cityofmillvalley.gov/2271/Lease-to-Locals-Pilot-Program',
    observedAt: '2026-04-22',
    sourceType: 'official',
  },
  {
    id: 'S31',
    title: '71 Roque Moraes Dr Apt 2',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/71-Roque-Moraes-Dr-APT-2-Mill-Valley-CA-94941/378965134_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S32',
    title: '1 Shelley Dr Apt B',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/1-Shelley-Dr-APT-B-Mill-Valley-CA-94941/461414512_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S33',
    title: '17 Shelley Dr #6',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Shelley-Dr-6-Mill-Valley-CA-94941/2075622546_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
    drawerSummary: 'Nearby 1BR walk-up that stair-stepped down before removal.',
  },
  {
    id: 'S34',
    title: '17 Shelley Dr #2',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Shelley-Dr-2-Mill-Valley-CA-94941/2070408617_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S35',
    title: '17 Shelley Dr',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/17-Shelley-Dr-Mill-Valley-CA-94941/80736873_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S36',
    title: '150 Seminary Dr Apt 2A',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/150-Seminary-Dr-APT-2A-Mill-Valley-CA-94941/19262447_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
    drawerSummary: 'Condo-grade 1BR ceiling comp with repeated $2,995 removals.',
  },
  {
    id: 'S37',
    title: '160 Seminary Dr Apt 1-F',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/160-Seminary-Dr-APT-1-F-Mill-Valley-CA-94941/19262463_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S38',
    title: '160 Seminary Dr U-3F',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/160-Seminary-Dr-U-3F-Mill-Valley-CA-94941/19262475_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S39',
    title: '25 Roque Moraes Ct #1',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/25-Roque-Moraes-Ct-1-Mill-Valley-CA-94941/2059545791_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S40',
    title: '25 Roque Moraes Ct #3',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/25-Roque-Moraes-Ct-3-Mill-Valley-CA-94941/2069085333_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S41',
    title: '49 Roque Moraes Ct Apt 6',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/49-Roque-Moraes-Ct-APT-6-Mill-Valley-CA-94941/2082581909_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S42',
    title: '33 Roque Moraes Ct #6',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/33-Roque-Moraes-Ct-6-Mill-Valley-CA-94941/447480640_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S43',
    title: '33 Roque Moraes Ct',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/33-Roque-Moraes-Ct-Mill-Valley-CA-94941/80734346_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S44',
    title: '61 Roque Moraes Dr',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/61-Roque-Moraes-Dr-Mill-Valley-CA-94941/80737145_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S45',
    title: '55 Roque Moraes Ct Apt 1',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/55-Roque-Moraes-Ct-APT-1-Mill-Valley-CA-94941/2101369217_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S46',
    title: '55 Roque Moraes Ct Apt 3',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/55-Roque-Moraes-Ct-APT-3-Mill-Valley-CA-94941/2094598588_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S47',
    title: '61 Roque Moraes Dr #D',
    domain: 'zillow.com',
    url: 'https://www.zillow.com/homedetails/61-Roque-Moraes-Dr-D-Mill-Valley-CA-94941/457203417_zpid/',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
  {
    id: 'S48',
    title: '55 Roque Moraes Ct Redfin Building',
    domain: 'redfin.com',
    url: 'https://www.redfin.com/CA/Mill-Valley/55-Roque-Moraes-Ct-94941/home/1228785',
    observedAt: '2026-04-22',
    sourceType: 'public_portal',
  },
];

export const recommendationScenarios: RecommendationScenario[] = [
  {
    id: 'fast',
    label: 'Fast Lease-Up',
    askRent: '$2,745',
    effectiveRent: '$2,695-$2,745',
    confidence: 'High-moderate',
    posture: 'Price just above the strongest same-street and micro-area floor signals to reduce vacancy risk.',
    rationale:
      'This sits above the strongest same-street off-market evidence around $2,600 while staying well below the premium condo and institutional ceiling.',
    triggers: [
      'Use when vacancy cost matters more than extracting the last $100 to $150 of face rent.',
      'Hold for 7 days if qualified inquiry volume is healthy.',
      'If response is slow, add a move-in credit before cutting the face rent.',
    ],
    sourceIds: ['S1', 'S3', 'S24', 'S33', 'S34'],
  },
  {
    id: 'balanced',
    label: 'Balanced Target',
    askRent: '$2,895',
    effectiveRent: '$2,845-$2,895',
    confidence: 'Moderate',
    posture: 'Aim above the same-unit and same-street 2024-2025 off-market evidence, but stay below stronger feature packages already asking near $3,000.',
    rationale:
      'Supported by active commuter-corridor comps and the spring window, but capped by repeated off-market evidence that Roque Moraes product gets punished when it stretches too high.',
    triggers: [
      'Default starting point if Unit 1 still matches the March 2025 public baseline.',
      'Reassess after 7-10 days with weak qualified inquiry volume.',
      'Prefer a one-time concession before moving below $2,845 effective.',
    ],
    sourceIds: ['S18', 'S20', 'S21', 'S24', 'S36', 'S37'],
  },
  {
    id: 'stretch',
    label: 'Stretch',
    askRent: '$2,995',
    effectiveRent: '$2,925-$2,995',
    confidence: 'Moderate-low',
    posture: 'Use only if the unit now presents materially better than the 2025 public listing and the deck/view read unusually well.',
    rationale:
      'This is already ceiling territory occupied by better-renovated or condo-grade product with in-unit laundry, stronger parking, or stronger building amenities.',
    triggers: [
      'Only defensible if the current photos and finish level beat the public baseline.',
      'Cut or concede quickly if the first 7-10 days are soft.',
      'Do not drift above this level without new evidence.',
    ],
    sourceIds: ['S21', 'S22', 'S23', 'S36', 'S37'],
  },
];

export const buildingFacts: BuildingFact[] = [
  {
    field: 'Property type',
    chosenValue: 'Six-unit multifamily apartment building',
    confidence: 'High',
    contradiction: 'Low',
    sourceIds: ['S9', 'S10', 'S12', 'S14', 'S17'],
  },
  {
    field: 'Unit mix',
    chosenValue: '2 x 1BR/1BA and 4 x 2BR/1BA',
    confidence: 'High',
    contradiction: 'Low',
    sourceIds: ['S12', 'S13', 'S17'],
  },
  {
    field: 'Year built',
    chosenValue: '1958',
    confidence: 'High',
    contradiction: 'Low',
    sourceIds: ['S9', 'S10', 'S12', 'S14'],
  },
  {
    field: 'Building size',
    chosenValue: '4,576 sqft',
    confidence: 'High',
    contradiction: 'Low',
    sourceIds: ['S9', 'S10', 'S11', 'S12'],
  },
  {
    field: 'Stories',
    chosenValue: 'Mixed evidence: 2 or 3 stories',
    confidence: 'Moderate',
    contradiction: 'High',
    sourceIds: ['S9', 'S10', 'S12', 'S13', 'S17'],
  },
  {
    field: 'Parking',
    chosenValue: '12 spaces with 6 covered carports plus additional off-street parking language',
    confidence: 'Moderate',
    contradiction: 'Medium',
    sourceIds: ['S9', 'S10', 'S11', 'S14', 'S17'],
  },
  {
    field: 'Shared features',
    chosenValue: 'Common laundry, private decks/balconies, storage, Mt. Tam / panoramic views',
    confidence: 'High',
    contradiction: 'Low',
    sourceIds: ['S9', 'S10', 'S13', 'S17'],
  },
  {
    field: 'Lot size',
    chosenValue: 'Best-supported lot size: 8,864-8,865 sqft',
    confidence: 'Moderate',
    contradiction: 'High',
    sourceIds: ['S9', 'S10', 'S11', 'S16'],
  },
];

export const buildingTimeline: TimelineEvent[] = [
  {
    date: '1958',
    title: 'Building completed',
    details: 'Public portals consistently identify 1958 as the construction year.',
    sourceIds: ['S9', 'S10', 'S12', 'S14'],
  },
  {
    date: '1986-05-06',
    title: 'Sale at $550,000',
    details: 'Earliest consistently surfaced public-record sale in the portal record.',
    sourceIds: ['S10', 'S14'],
  },
  {
    date: '1990-03-28',
    title: 'Sale at $750,000',
    details: 'Public-record transaction repeated across Redfin, Realtor, and Compass.',
    sourceIds: ['S10', 'S11', 'S14'],
  },
  {
    date: '1992-07-14',
    title: 'Sale at $810,000',
    details: 'Latest pre-2010 public-record sale consistently surfaced across portals.',
    sourceIds: ['S10', 'S14'],
  },
  {
    date: '2010-05-17',
    title: 'Listed for sale at $1,195,000',
    details: 'Compass and Realtor show a May 17 list date; public-record style portals disagree slightly on the later close.',
    sourceIds: ['S11', 'S14'],
  },
  {
    date: '2010-06-20',
    title: 'Pending sale',
    details: 'MLS-style status moved to pending ahead of the eventual 2010 close.',
    sourceIds: ['S11', 'S14'],
  },
  {
    date: '2012-11-13',
    title: 'Permit: remove and replace windows',
    details: 'Redfin permit log records the building permit activity.',
    sourceIds: ['S10'],
  },
  {
    date: '2016-03-21',
    title: 'Permit: reroofing',
    details: 'Roof work appears in Redfin permit history and in marketing copy.',
    sourceIds: ['S10', 'S17'],
  },
  {
    date: '2022-10-07',
    title: 'Permit: stair and landing repairs',
    details: 'Later building maintenance appears in the public permit log.',
    sourceIds: ['S10'],
  },
  {
    date: '2025-09-29',
    title: 'Listed for sale at $2,900,000',
    details: 'Sale marketing leaned on rent upside, views, parking, storage, and deck certifications.',
    sourceIds: ['S9', 'S11', 'S12', 'S14', 'S17'],
  },
  {
    date: '2025-10-29',
    title: 'Price cut to $2,750,000',
    details: 'Broad portal agreement on the October price reduction.',
    sourceIds: ['S9', 'S10', 'S11', 'S14'],
  },
  {
    date: '2025-12-19 to 2025-12-24',
    title: 'Status sequence diverges by portal',
    details: 'Pending, removed, expired, and relisted dates do not fully align across Compass, Zillow, Redfin, and Realtor.',
    sourceIds: ['S9', 'S10', 'S11', 'S14'],
  },
  {
    date: '2026-02-11',
    title: 'Sold at $2,600,000',
    details: 'The 2025-2026 sale cycle closed materially below the first ask, reinforcing the caution against leaning too hard on upside language.',
    sourceIds: ['S9', 'S10', 'S11', 'S14'],
  },
];

export const units: UnitRecord[] = [
  {
    id: 'unit-1',
    label: 'Unit 1',
    bedrooms: '1',
    bathrooms: '1',
    status: 'Vacant pricing target',
    traceStatus: 'Confirmed public unit history',
    baselineNotes:
      'March 2025 public listing describes a remodeled 1BR/1BA with private deck, Mt. Tam view, storage, on-site laundry, and covered parking.',
    inferredFeatures: [
      'Private deck',
      'Mt. Tam view',
      'Storage room',
      'On-site laundry',
      'Covered or off-street parking',
      'No pets on HotPads mirror',
    ],
    ledger: [
      {
        date: '2025-01-24',
        price: '$2,675',
        eventType: 'Listed for rent',
        portals: ['Zillow', 'Trulia'],
        notes:
          'Beautifully remodeled 1BR/1BA in a 6-plex with private deck, storage room, covered parking, and on-site laundry.',
        sourceIds: ['S1', 'S2'],
      },
      {
        date: '2025-02-28',
        price: '$2,675',
        eventType: 'Listing removed',
        portals: ['Zillow', 'Trulia'],
        notes: 'Portal event only; not proof of signed lease rent.',
        sourceIds: ['S1', 'S2'],
      },
      {
        date: '2025-03-12',
        price: '$2,575',
        eventType: 'Relisted',
        portals: ['Zillow', 'Trulia', 'HotPads'],
        notes:
          'Same feature package; HotPads preserves the $2,575 ask and flags no pets, off-street parking, refrigerator, and on-site laundry.',
        sourceIds: ['S1', 'S2', 'S3'],
      },
      {
        date: '2025-03-25',
        price: '$2,575',
        eventType: 'Taken off market',
        portals: ['Zillow', 'Trulia', 'HotPads'],
        notes: 'Fastest traction in the visible public sequence came after the $100 price cut.',
        sourceIds: ['S1', 'S2', 'S3'],
      },
    ],
    supportingEvidence: [
      {
        summary:
          'Public trace is concentrated in Zillow Rentals and mirrored into Trulia and HotPads. No reliable public square footage surfaced.',
        sourceIds: ['S1', 'S2', 'S3'],
      },
      {
        summary:
          'Same-street 1BR at 25 Roque Moraes Ct Apt 6 reinforces the same price-pressure pattern: the visible market improved after a move from $2,700 to $2,600.',
        sourceIds: ['S24'],
      },
      {
        summary:
          'Same-building sale marketing later advertised upside in rents, but the 2026 sale still closed materially below the original ask.',
        sourceIds: ['S9', 'S10', 'S14'],
      },
    ],
  },
  {
    id: 'unit-2',
    label: 'Unit 2',
    bedrooms: '2',
    bathrooms: '1',
    status: 'Occupied or off market',
    traceStatus: 'Confirmed public unit history',
    baselineNotes:
      'Public record shows Unit 2 as an upstairs 2BR/1BA with deck, storage, on-site laundry, and covered parking.',
    inferredFeatures: [
      'Private deck',
      'Storage room',
      'On-site laundry',
      'Covered parking',
      'Walkability to Whole Foods and local rec amenities',
    ],
    ledger: [
      {
        date: '2025-02-17',
        price: '$2,875',
        eventType: 'Listed for rent',
        portals: ['Zillow', 'Trulia'],
        notes:
          'Description mentions an upstairs 2BR/1BA with private deck, on-site laundry, storage, and covered parking.',
        sourceIds: ['S4', 'S5'],
      },
      {
        date: '2025-03-12',
        price: '$2,775',
        eventType: 'Price cut',
        portals: ['Zillow', 'Trulia', 'HotPads'],
        notes:
          'Trulia rounds the cut differently and the body copy still references $2,875 in one stale line.',
        sourceIds: ['S4', 'S5', 'S6'],
      },
      {
        date: '2025-03-17',
        price: '$2,775',
        eventType: 'Taken off market',
        portals: ['Zillow', 'Trulia', 'HotPads'],
        notes: 'Another same-building example of traction improving after a modest cut.',
        sourceIds: ['S4', 'S5', 'S6'],
      },
    ],
    supportingEvidence: [
      {
        summary:
          'Unit 2 body copy has an internal pricing inconsistency, likely from stale text that was not updated after the cut.',
        sourceIds: ['S4', 'S5', 'S6'],
      },
    ],
  },
  {
    id: 'unit-3',
    label: 'Unit 3',
    bedrooms: '2',
    bathrooms: '1',
    status: 'Unknown from public trace',
    traceStatus: 'No direct public unit trace found',
    baselineNotes:
      'Unit 3 likely exists as one of the four 2BR units in the building mix, but no exact public unit page was located.',
    inferredFeatures: [
      'Likely part of the 2BR/1BA inventory',
      'Building-level features may apply, but not confirmed at unit level',
    ],
    ledger: [],
    supportingEvidence: [
      {
        summary:
          'Building-level sources consistently show a six-unit property with four 2BR units, but no indexed Unit 3 page surfaced on Zillow, HotPads, Trulia, Realtor, Homes.com, Compass, or Redfin.',
        sourceIds: ['S9', 'S11', 'S12', 'S13', 'S14', 'S15'],
      },
    ],
    portalsSearched: [
      'Zillow',
      'HotPads',
      'Trulia',
      'Realtor',
      'Apartments.com',
      'Homes.com',
      'Compass',
      'Redfin',
    ],
  },
  {
    id: 'unit-4',
    label: 'Unit 4',
    bedrooms: '2',
    bathrooms: '1',
    status: 'Occupied or off market',
    traceStatus: 'Confirmed public unit history',
    baselineNotes:
      'Unit 4 shows a short but useful public rent cycle in late 2024 at the top of the building’s public 2BR rent band.',
    inferredFeatures: [
      'Remodeled upstairs layout',
      'Private deck',
      'Shared laundry',
      'Covered or attached parking',
      'Water and garbage included',
    ],
    ledger: [
      {
        date: '2024-11-01',
        price: '$3,000',
        eventType: 'Listed for rent',
        portals: ['Zillow'],
        notes:
          'Remodeled upstairs 2BR/1BA in 6-plex with deck, storage, shared laundry, and parking.',
        sourceIds: ['S7'],
      },
      {
        date: '2024-11-24',
        price: '$3,000',
        eventType: 'Listing removed',
        portals: ['Zillow'],
        notes: 'Zillow removal date differs from HotPads by one week.',
        sourceIds: ['S7'],
      },
      {
        date: '2024-12-01',
        price: '$3,000',
        eventType: 'Taken off market',
        portals: ['HotPads'],
        notes: 'Likely syndication lag rather than a different market event.',
        sourceIds: ['S8'],
      },
    ],
    supportingEvidence: [
      {
        summary:
          'Compass and Homes.com expose a 2BR rent range of $2,640-$3,000 for the building, placing Unit 4 at the top end of that visible band.',
        sourceIds: ['S11', 'S15'],
      },
    ],
  },
  {
    id: 'unit-5',
    label: 'Unit 5',
    bedrooms: 'Unknown',
    bathrooms: 'Unknown',
    status: 'Unknown from public trace',
    traceStatus: 'No direct public unit trace found',
    baselineNotes:
      'No exact Unit 5 page surfaced across the major portals searched in this research pass.',
    inferredFeatures: [
      'Exists within the six-unit mix',
      'May share building-level amenities',
    ],
    ledger: [],
    supportingEvidence: [
      {
        summary:
          'Portals searched for Unit 5 include Zillow, Redfin, Compass, LoopNet, Apartments.com, HotPads, Realtor, Homes.com, and Trulia with no exact unit page found.',
        sourceIds: ['S9', 'S10', 'S11', 'S12', 'S13', 'S14', 'S15'],
      },
    ],
    portalsSearched: [
      'Zillow',
      'Redfin',
      'Compass',
      'LoopNet',
      'Apartments.com',
      'HotPads',
      'Realtor',
      'Homes.com',
      'Trulia',
    ],
  },
  {
    id: 'unit-6',
    label: 'Unit 6',
    bedrooms: 'Unknown',
    bathrooms: 'Unknown',
    status: 'Unknown from public trace',
    traceStatus: 'No direct public unit trace found',
    baselineNotes:
      'No exact Unit 6 page surfaced across the major portals searched in this research pass.',
    inferredFeatures: [
      'Exists within the six-unit mix',
      'May share building-level amenities',
    ],
    ledger: [],
    supportingEvidence: [
      {
        summary:
          'Same search result as Unit 5: strong building-level visibility, but no exact Unit 6 listing page in the current public footprint.',
        sourceIds: ['S9', 'S10', 'S11', 'S12', 'S13', 'S14', 'S15'],
      },
    ],
    portalsSearched: [
      'Zillow',
      'Redfin',
      'Compass',
      'LoopNet',
      'Apartments.com',
      'HotPads',
      'Realtor',
      'Homes.com',
      'Trulia',
    ],
  },
];

export const comparables: ComparableRecord[] = [
  {
    id: 'comp-unit1',
    label: '17 Roque Moraes Ct #1',
    address: '17 Roque Moraes Ct #1, Mill Valley',
    bedBath: '1/1',
    sqft: 'Not surfaced',
    observedAt: '2026-04-22',
    role: 'primary',
    fit: 'Same unit',
    features:
      'Remodeled 1BR, private deck, Mt. Tam view, storage, shared laundry, covered parking.',
    adjustment:
      'Highest-weight evidence because it is the exact unit and shows the 2025 repricing path directly.',
    currentAsk: '$2,575',
    firstSeenAt: '2025-01-24',
    lastSeenAt: '2025-03-25',
    daysVisible: '35 days first cycle, 13 days second cycle',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'high',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2025-01-24',
        status: 'active',
        ask: '$2,675',
        note: 'Initial public listing for rent.',
        sourceIds: ['S1', 'S2'],
      },
      {
        date: '2025-02-28',
        status: 'removed',
        ask: '$2,675',
        note: 'Listing removed without public lease confirmation.',
        sourceIds: ['S1', 'S2'],
      },
      {
        date: '2025-03-12',
        status: 'relisted',
        ask: '$2,575',
        note: 'Relisted after a $100 cut.',
        sourceIds: ['S1', 'S2', 'S3'],
      },
      {
        date: '2025-03-25',
        status: 'off_market',
        ask: '$2,575',
        note: 'Taken off market on HotPads and removed on Zillow.',
        sourceIds: ['S1', 'S2', 'S3'],
      },
    ],
    sourceIds: ['S1', 'S2', 'S3'],
  },
  {
    id: 'comp-roque-25-6',
    label: '25 Roque Moraes Ct Apt 6',
    address: '25 Roque Moraes Ct Apt 6, Mill Valley',
    bedBath: '1/1',
    sqft: '742',
    observedAt: '2026-04-22',
    role: 'primary',
    fit: 'Strongest same-street analog',
    features:
      'Renovated, deck, Mt. Tam/open-space views, assigned carport, storage, coin laundry, water/garbage included.',
    adjustment:
      'Best same-street 1BR analog. Similar deck/view/storage/shared-laundry package but with known square footage.',
    currentAsk: '$2,600',
    firstSeenAt: '2024-07-04',
    lastSeenAt: '2024-11-24',
    daysVisible: '72-day first cycle, 10-day second cycle',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'high',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2024-07-04',
        status: 'active',
        ask: '$2,700',
        note: 'Initial listing.',
        sourceIds: ['S24'],
      },
      {
        date: '2024-08-05',
        status: 'price_cut',
        ask: '$2,600',
        note: 'Price cut after visible resistance at $2,700.',
        sourceIds: ['S24'],
      },
      {
        date: '2024-09-14',
        status: 'removed',
        ask: '$2,600',
        note: 'First cycle removed.',
        sourceIds: ['S24'],
      },
      {
        date: '2024-11-14',
        status: 'relisted',
        ask: '$2,600',
        note: 'Relisted at the lower ask.',
        sourceIds: ['S24'],
      },
      {
        date: '2024-11-24',
        status: 'off_market',
        ask: '$2,600',
        note: 'Second cycle removed.',
        sourceIds: ['S24'],
      },
    ],
    sourceIds: ['S24'],
  },
  {
    id: 'comp-shelley-4',
    label: '17 Shelley Dr Apt 4',
    address: '17 Shelley Dr Apt 4, Mill Valley',
    bedBath: '1/1',
    sqft: '603',
    observedAt: '2026-04-22',
    role: 'primary',
    fit: 'Best lower-bound active micro-area comp',
    features:
      'Updated kitchen and bath, private deck, community laundry, carport, storage, water and garbage included.',
    adjustment:
      'Same micro-neighborhood but smaller and likely a bit more basic than Unit 1.',
    currentAsk: '$2,295',
    firstSeenAt: '2025-12-24',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2024-09-10',
        status: 'active',
        ask: '$2,195',
        note: 'Earlier visible listing cycle.',
        sourceIds: ['S18'],
      },
      {
        date: '2024-09-11',
        status: 'removed',
        ask: '$2,195',
        note: 'Removed quickly in 2024.',
        sourceIds: ['S18'],
      },
      {
        date: '2025-12-24',
        status: 'relisted',
        ask: '$2,295',
        note: 'Back on market as an active comp.',
        sourceIds: ['S18'],
      },
    ],
    sourceIds: ['S18'],
  },
  {
    id: 'comp-shelley-6',
    label: '17 Shelley Dr #6',
    address: '17 Shelley Dr #6, Mill Valley',
    bedBath: '1/1',
    sqft: '619',
    observedAt: '2026-04-22',
    role: 'secondary',
    fit: 'Nearby 1BR walk-up floor',
    features:
      'Water and garbage included, simpler shared-laundry walk-up product, no strong parking/value story surfaced.',
    adjustment:
      'Lower-tier product that helps define the micro-area floor rather than the likely Unit 1 clearing band.',
    currentAsk: '$1,995',
    firstSeenAt: '2024-03-13',
    lastSeenAt: '2024-05-14',
    daysVisible: 'Around 62 days',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2024-03-13',
        status: 'active',
        ask: '$2,350',
        note: 'Initial listing.',
        sourceIds: ['S33'],
      },
      {
        date: '2024-03-20',
        status: 'price_cut',
        ask: '$2,150',
        note: 'First cut.',
        sourceIds: ['S33'],
      },
      {
        date: '2024-04-20',
        status: 'price_cut',
        ask: '$1,995',
        note: 'Second cut.',
        sourceIds: ['S33'],
      },
      {
        date: '2024-05-14',
        status: 'off_market',
        ask: '$1,995',
        note: 'Removed without public lease confirmation.',
        sourceIds: ['S33'],
      },
    ],
    sourceIds: ['S33'],
  },
  {
    id: 'comp-shelley-2',
    label: '17 Shelley Dr #2',
    address: '17 Shelley Dr #2, Mill Valley',
    bedBath: '1/1',
    sqft: '612',
    observedAt: '2026-04-22',
    role: 'secondary',
    fit: 'Historical nearby 1BR floor',
    features:
      'Carport, community laundry, water and garbage included, simpler small-building stock.',
    adjustment:
      'Older signal but useful for the lower bound of nearby smaller 1BR product.',
    currentAsk: '$1,980',
    firstSeenAt: '2021-06-05',
    lastSeenAt: '2021-07-10',
    daysVisible: 'About 35 days',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2021-06-05',
        status: 'active',
        ask: '$2,195',
        note: 'Initial listing.',
        sourceIds: ['S34'],
      },
      {
        date: '2021-06-22',
        status: 'price_cut',
        ask: '$2,050',
        note: 'First cut.',
        sourceIds: ['S34'],
      },
      {
        date: '2021-07-02',
        status: 'price_cut',
        ask: '$1,980',
        note: 'Second cut.',
        sourceIds: ['S34'],
      },
      {
        date: '2021-07-10',
        status: 'off_market',
        ask: '$1,980',
        note: 'Removed from the public feed.',
        sourceIds: ['S34'],
      },
    ],
    sourceIds: ['S34'],
  },
  {
    id: 'comp-shelley-small',
    label: '17 Shelley Dr',
    address: '17 Shelley Dr, Mill Valley',
    bedBath: '1/1',
    sqft: '500',
    observedAt: '2026-04-22',
    role: 'secondary',
    fit: 'Historical micro-area floor',
    features:
      'Remodeled kitchen, upgraded bath, large private deck, Horse Hill view, walk-in closet, off-street parking, on-site laundry.',
    adjustment:
      'Very small but shows a historical floor for deck-and-view Shelley stock.',
    currentAsk: '$1,950',
    firstSeenAt: '2019-03-19',
    lastSeenAt: '2019-05-18',
    daysVisible: 'About 60 days',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2019-03-19',
        status: 'active',
        ask: '$1,995',
        note: 'Initial listing.',
        sourceIds: ['S35'],
      },
      {
        date: '2019-05-04',
        status: 'price_cut',
        ask: '$1,950',
        note: 'Small cut before removal.',
        sourceIds: ['S35'],
      },
      {
        date: '2019-05-18',
        status: 'off_market',
        ask: '$1,950',
        note: 'Removed from the public feed.',
        sourceIds: ['S35'],
      },
    ],
    sourceIds: ['S35'],
  },
  {
    id: 'comp-redwood-2',
    label: '1030 Redwood Hwy #2',
    address: '1030 Redwood Hwy #2, Mill Valley',
    bedBath: '1/1',
    sqft: '582',
    observedAt: '2026-04-22',
    role: 'secondary',
    fit: 'Middle active commuter-side comp',
    features:
      'Recently renovated, covered parking, storage, pool, fire pits, and outdoor kitchen.',
    adjustment:
      'Smaller than Unit 1 but with stronger community amenities. Good support for the balanced band.',
    currentAsk: '$2,799-$2,850',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$2,799-$2,850',
        note: 'Current active listing range from the project page.',
        sourceIds: ['S20'],
      },
    ],
    sourceIds: ['S20'],
  },
  {
    id: 'comp-trailhead-216d',
    label: 'Trailhead 214 Villa Garden Dr #216D',
    address: '214 Villa Garden Dr #216D, Mill Valley',
    bedBath: '1/1',
    sqft: '668',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Institutional upper-middle benchmark',
    features:
      'Covered parking, pool, quartz and stainless finishes, hard-surface flooring, pet-friendly community.',
    adjustment:
      'Useful ceiling check, but not apples-to-apples with older small-building stock.',
    currentAsk: '$2,995',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$2,995',
        note: 'Current active listing in a larger amenity-rich community.',
        sourceIds: ['S21'],
      },
    ],
    sourceIds: ['S21'],
  },
  {
    id: 'comp-reed-9d',
    label: '37 Reed Blvd #9D',
    address: '37 Reed Blvd #9D, Mill Valley',
    bedBath: '1/1',
    sqft: '700',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Brand-new renovation ceiling comp',
    features:
      'Brand-new renovation, in-unit washer/dryer, dishwasher, detached parking, extra storage, pet-friendly.',
    adjustment:
      'Materially better interior package than the March 2025 Unit 1 baseline.',
    currentAsk: '$2,995',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$2,995',
        note: 'Current active listing for a stronger package.',
        sourceIds: ['S22'],
      },
    ],
    sourceIds: ['S22'],
  },
  {
    id: 'comp-chateau-e2',
    label: 'Chateau Chillon Apt E2',
    address: '10 Milland Dr Apt E2, Mill Valley',
    bedBath: '1/1',
    sqft: '710',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Premium benchmark',
    features:
      'Shared laundry, covered parking, renovated kitchen and bath, pool, gym, garden setting.',
    adjustment:
      'Premium benchmark in the same submarket, but clearly above the subject in amenities.',
    currentAsk: '$3,299',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$3,299',
        note: 'Current active premium 1BR benchmark.',
        sourceIds: ['S23'],
      },
    ],
    sourceIds: ['S23'],
  },
  {
    id: 'comp-seminary-2a',
    label: '150 Seminary Dr Apt 2A',
    address: '150 Seminary Dr Apt 2A, Mill Valley',
    bedBath: '1/1',
    sqft: '789',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Condo-grade ceiling comp',
    features:
      'In-unit laundry, granite counters, garage parking, water/garbage included, Strawberry Cove condo product.',
    adjustment:
      'Useful upper-tier ceiling, but materially stronger than Unit 1 on product class and amenities.',
    currentAsk: '$2,995',
    firstSeenAt: '2020-07-23',
    lastSeenAt: '2022-07-21',
    daysVisible: 'Repeated removals at $2,995 across two cycles',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2020-07-23',
        status: 'active',
        ask: '$2,995',
        note: 'Older visible active cycle.',
        sourceIds: ['S36'],
      },
      {
        date: '2020-08-05',
        status: 'off_market',
        ask: '$2,995',
        note: 'Removed from the public feed.',
        sourceIds: ['S36'],
      },
      {
        date: '2022-06-24',
        status: 'relisted',
        ask: '$2,995',
        note: 'Second visible cycle at the same ask.',
        sourceIds: ['S36'],
      },
      {
        date: '2022-07-21',
        status: 'off_market',
        ask: '$2,995',
        note: 'Removed again without public lease confirmation.',
        sourceIds: ['S36'],
      },
    ],
    sourceIds: ['S36'],
  },
  {
    id: 'comp-seminary-1f',
    label: '160 Seminary Dr Apt 1-F',
    address: '160 Seminary Dr Apt 1-F, Mill Valley',
    bedBath: '1/1',
    sqft: '789',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Upper premium condo comp',
    features:
      'Two decks, one water view, in-unit washer/dryer, interior parking, storage, elevator, pool/spa.',
    adjustment:
      'Very useful overpricing ceiling because it had to come down from $3,200 to $2,995 before going off market.',
    currentAsk: '$2,995',
    firstSeenAt: '2024-04-24',
    lastSeenAt: '2025-12-08',
    daysVisible: 'About 32 days in 2024 plus a short 2025 cycle',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2024-04-24',
        status: 'active',
        ask: '$3,200',
        note: 'Initial premium ask.',
        sourceIds: ['S37'],
      },
      {
        date: '2024-05-07',
        status: 'price_cut',
        ask: '$3,100',
        note: 'First cut.',
        sourceIds: ['S37'],
      },
      {
        date: '2024-05-16',
        status: 'price_cut',
        ask: '$2,995',
        note: 'Second cut.',
        sourceIds: ['S37'],
      },
      {
        date: '2024-05-26',
        status: 'off_market',
        ask: '$2,995',
        note: 'Removed from the public feed.',
        sourceIds: ['S37'],
      },
      {
        date: '2025-12-05',
        status: 'relisted',
        ask: '$3,100',
        note: 'Later cycle at a higher ask.',
        sourceIds: ['S37'],
      },
      {
        date: '2025-12-08',
        status: 'off_market',
        ask: '$3,100',
        note: 'Removed quickly in the later cycle.',
        sourceIds: ['S37'],
      },
    ],
    sourceIds: ['S37'],
  },
  {
    id: 'comp-seminary-u3f',
    label: '160 Seminary Dr U-3F',
    address: '160 Seminary Dr U-3F, Mill Valley',
    bedBath: '1/1',
    sqft: '789',
    observedAt: '2026-04-22',
    role: 'ceiling',
    fit: 'Historical condo ceiling floor',
    features:
      'Partial bay view, fireplace, in-unit laundry, assigned parking, water/garbage included.',
    adjustment:
      'Older low-weight context showing how Seminary condo stock sat far above Shelley but below the more recent premium cycles.',
    currentAsk: '$2,150',
    firstSeenAt: '2017-08-29',
    lastSeenAt: '2021-04-23',
    daysVisible: 'Two short historical cycles',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2017-08-29',
        status: 'active',
        ask: '$2,150',
        note: 'First visible cycle.',
        sourceIds: ['S38'],
      },
      {
        date: '2017-09-09',
        status: 'off_market',
        ask: '$2,150',
        note: 'Removed quickly.',
        sourceIds: ['S38'],
      },
      {
        date: '2021-04-07',
        status: 'relisted',
        ask: '$2,150',
        note: 'Later visible cycle at the same ask.',
        sourceIds: ['S38'],
      },
      {
        date: '2021-04-23',
        status: 'off_market',
        ask: '$2,150',
        note: 'Removed from the public feed.',
        sourceIds: ['S38'],
      },
    ],
    sourceIds: ['S38'],
  },
  {
    id: 'comp-unit2',
    label: '17 Roque Moraes Ct Apt 2',
    address: '17 Roque Moraes Ct Apt 2, Mill Valley',
    bedBath: '2/1',
    sqft: 'Not surfaced',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-building 2BR calibration',
    features:
      'Upstairs, private deck, shared laundry, off-street parking, storage.',
    adjustment:
      'Strongest same-building 2BR calibration for the subject property ladder.',
    currentAsk: '$2,775',
    firstSeenAt: '2025-02-17',
    lastSeenAt: '2025-03-17',
    daysVisible: 'About 28 days',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'high',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2025-02-17',
        status: 'active',
        ask: '$2,875',
        note: 'Initial listing.',
        sourceIds: ['S4', 'S5'],
      },
      {
        date: '2025-03-12',
        status: 'price_cut',
        ask: '$2,775',
        note: 'Cut after visible time on market.',
        sourceIds: ['S4', 'S5', 'S6'],
      },
      {
        date: '2025-03-17',
        status: 'off_market',
        ask: '$2,775',
        note: 'Removed from the public feed.',
        sourceIds: ['S4', 'S5', 'S6'],
      },
    ],
    sourceIds: ['S4', 'S5', 'S6'],
  },
  {
    id: 'comp-unit4',
    label: '17 Roque Moraes Ct #4',
    address: '17 Roque Moraes Ct #4, Mill Valley',
    bedBath: '2/1',
    sqft: 'Not surfaced',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-building 2BR ceiling calibration',
    features:
      'Remodeled upstairs layout, private deck, storage, shared laundry, covered parking, water/garbage included.',
    adjustment:
      'Shows the same-building 2BR ceiling and helps frame how narrow the internal 1BR-to-2BR spread can be.',
    currentAsk: '$3,000',
    firstSeenAt: '2024-11-01',
    lastSeenAt: '2024-12-01',
    daysVisible: 'About 3 to 4 weeks',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'high',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2024-11-01',
        status: 'active',
        ask: '$3,000',
        note: 'Initial listing.',
        sourceIds: ['S7'],
      },
      {
        date: '2024-11-24',
        status: 'removed',
        ask: '$3,000',
        note: 'Zillow removal date.',
        sourceIds: ['S7'],
      },
      {
        date: '2024-12-01',
        status: 'off_market',
        ask: '$3,000',
        note: 'HotPads off-market date.',
        sourceIds: ['S8'],
      },
    ],
    sourceIds: ['S7', 'S8'],
  },
  {
    id: 'comp-25-1',
    label: '25 Roque Moraes Ct #1',
    address: '25 Roque Moraes Ct #1, Mill Valley',
    bedBath: '2/1',
    sqft: '792',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street 2BR analog',
    features:
      'Floor-to-ceiling windows, covered deck with views, covered parking, private storage, coin laundry, water/garbage included.',
    adjustment:
      'Very useful sister-building 2BR context on the same street.',
    currentAsk: '$2,800',
    firstSeenAt: '2023-02-02',
    lastSeenAt: '2023-04-07',
    daysVisible: 'About 64 days',
    outcomeStatus: 'historical',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2023-02-02',
        status: 'active',
        ask: '$2,800',
        note: 'Initial listing.',
        sourceIds: ['S39'],
      },
      {
        date: '2023-04-07',
        status: 'off_market',
        ask: '$2,800',
        note: 'Removed from the public feed.',
        sourceIds: ['S39'],
      },
    ],
    sourceIds: ['S39'],
  },
  {
    id: 'comp-25-3',
    label: '25 Roque Moraes Ct #3',
    address: '25 Roque Moraes Ct #3, Mill Valley',
    bedBath: '2/1',
    sqft: '790',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Older same-street 2BR check',
    features:
      'Deck with filtered views, fresh paint, new carpeting, covered parking, storage, coin laundry.',
    adjustment:
      'Older and lower weight than the more recent same-street 2BRs.',
    currentAsk: '$2,500',
    firstSeenAt: '2021-08-08',
    lastSeenAt: '2021-08-30',
    daysVisible: 'About 22 days',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2021-08-08',
        status: 'active',
        ask: '$2,500',
        note: 'Initial listing.',
        sourceIds: ['S40'],
      },
      {
        date: '2021-08-30',
        status: 'off_market',
        ask: '$2,500',
        note: 'Removed from the public feed.',
        sourceIds: ['S40'],
      },
    ],
    sourceIds: ['S40'],
  },
  {
    id: 'comp-49-6',
    label: '49 Roque Moraes Ct Apt 6',
    address: '49 Roque Moraes Ct Apt 6, Mill Valley',
    bedBath: '2/1',
    sqft: '850',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street repricing example',
    features:
      'Top-floor deck-oriented product in the same street cluster.',
    adjustment:
      'Useful same-street evidence that Roque Moraes landlords have needed meaningful cuts before.',
    currentAsk: '$2,750',
    firstSeenAt: '2019-09-27',
    lastSeenAt: '2019-12-10',
    daysVisible: 'Multiple cycles in late 2019',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2019-09-27',
        status: 'active',
        ask: '$3,150',
        note: 'Initial listing.',
        sourceIds: ['S41'],
      },
      {
        date: '2019-10-08',
        status: 'removed',
        ask: '$3,150',
        note: 'First cycle removed.',
        sourceIds: ['S41'],
      },
      {
        date: '2019-11-13',
        status: 'relisted',
        ask: '$2,750',
        note: 'Relisted at a materially lower ask.',
        sourceIds: ['S41'],
      },
      {
        date: '2019-12-10',
        status: 'off_market',
        ask: '$2,750',
        note: 'Removed again after repricing.',
        sourceIds: ['S41'],
      },
    ],
    sourceIds: ['S41'],
  },
  {
    id: 'comp-55-1',
    label: '55 Roque Moraes Ct Apt 1',
    address: '55 Roque Moraes Ct Apt 1, Mill Valley',
    bedBath: '2/1',
    sqft: '870',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street stronger finish package',
    features:
      'Hardwood floors, granite counters, dishwasher, patio, carport, on-site laundry, storage, water/garbage included.',
    adjustment:
      'A better-finished same-street 2BR that helps define the upper Roque Moraes 2BR band.',
    currentAsk: '$3,050',
    firstSeenAt: '2023-11-10',
    lastSeenAt: '2024-01-08',
    daysVisible: 'About 59 days',
    outcomeStatus: 'historical',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2023-11-10',
        status: 'active',
        ask: '$3,050',
        note: 'Initial listing.',
        sourceIds: ['S45'],
      },
      {
        date: '2024-01-08',
        status: 'off_market',
        ask: '$3,050',
        note: 'Removed from the public feed.',
        sourceIds: ['S45'],
      },
    ],
    sourceIds: ['S45'],
  },
  {
    id: 'comp-55-3',
    label: '55 Roque Moraes Ct Apt 3',
    address: '55 Roque Moraes Ct Apt 3, Mill Valley',
    bedBath: '2/1',
    sqft: '850',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street stronger finish package with two cycles',
    features:
      'Updated kitchen and bath, stainless appliances, granite counters, hardwood floors, patio with views, garage or off-street parking.',
    adjustment:
      'Shows a better-finished same-street unit still took time and a reset to move.',
    currentAsk: '$3,050',
    firstSeenAt: '2021-04-20',
    lastSeenAt: '2022-03-16',
    daysVisible: 'Two historical cycles',
    outcomeStatus: 'historical',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2021-04-20',
        status: 'active',
        ask: '$2,900',
        note: 'First visible cycle.',
        sourceIds: ['S46'],
      },
      {
        date: '2021-05-12',
        status: 'removed',
        ask: '$2,900',
        note: 'Removed after the first cycle.',
        sourceIds: ['S46'],
      },
      {
        date: '2022-01-14',
        status: 'relisted',
        ask: '$3,050',
        note: 'Second visible cycle at a higher ask.',
        sourceIds: ['S46'],
      },
      {
        date: '2022-03-16',
        status: 'off_market',
        ask: '$3,050',
        note: 'Removed after the second cycle.',
        sourceIds: ['S46'],
      },
    ],
    sourceIds: ['S46'],
  },
  {
    id: 'comp-61-d',
    label: '61 Roque Moraes Dr #D',
    address: '61 Roque Moraes Dr #D, Mill Valley',
    bedBath: '2/1',
    sqft: '900',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Near-top-of-range same-street 2BR',
    features:
      'Top floor, vaulted wood-beamed ceilings, front and rear redwood decks, covered parking with tandem room, storage, shared laundry, water/garbage included.',
    adjustment:
      'Very strong same-street 2BR package that needed a small cut before removal.',
    currentAsk: '$3,200',
    firstSeenAt: '2025-10-04',
    lastSeenAt: '2025-12-22',
    daysVisible: 'About 79 days',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2025-10-04',
        status: 'active',
        ask: '$3,290',
        note: 'Initial listing.',
        sourceIds: ['S47'],
      },
      {
        date: '2025-10-31',
        status: 'price_cut',
        ask: '$3,200',
        note: 'Minor cut after the initial ask.',
        sourceIds: ['S47'],
      },
      {
        date: '2025-12-22',
        status: 'off_market',
        ask: '$3,200',
        note: 'Removed without public lease confirmation.',
        sourceIds: ['S47'],
      },
    ],
    sourceIds: ['S47'],
  },
  {
    id: 'comp-33-6',
    label: '33 Roque Moraes Ct #6',
    address: '33 Roque Moraes Ct #6, Mill Valley',
    bedBath: '2/1',
    sqft: '900',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street superior 2BR ceiling',
    features:
      'Renovated with stainless counters and appliances, in-unit laundry or hookups, stronger finish level.',
    adjustment:
      'Same street but clearly superior to the subject on finish package, so better used as a 2BR ceiling marker.',
    currentAsk: '$3,400',
    firstSeenAt: '2025-02-17',
    lastSeenAt: '2025-03-06',
    daysVisible: 'About 17 days',
    outcomeStatus: 'off_market_inferred',
    outcomeConfidence: 'moderate',
    outcomeLabel: 'Off-market inferred',
    lifecycle: [
      {
        date: '2025-02-17',
        status: 'active',
        ask: '$3,400',
        note: 'Initial listing.',
        sourceIds: ['S42'],
      },
      {
        date: '2025-03-06',
        status: 'off_market',
        ask: '$3,400',
        note: 'Removed quickly from the public feed.',
        sourceIds: ['S42'],
      },
    ],
    sourceIds: ['S42'],
  },
  {
    id: 'comp-33-building',
    label: '33 Roque Moraes Ct',
    address: '33 Roque Moraes Ct, Mill Valley',
    bedBath: '2/1',
    sqft: '950',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street repricing context',
    features:
      'Remodeled, granite and stainless finishes, in-unit washer/dryer, 1-car parking plus street parking.',
    adjustment:
      'Useful evidence that even same-street superior product has sometimes needed meaningful cuts.',
    currentAsk: '$3,000',
    firstSeenAt: '2020-09-12',
    lastSeenAt: '2023-09-09',
    daysVisible: 'Multiple historical cycles',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2020-09-12',
        status: 'active',
        ask: '$2,995',
        note: 'Older visible cycle.',
        sourceIds: ['S43'],
      },
      {
        date: '2023-08-10',
        status: 'relisted',
        ask: '$3,450',
        note: 'Later visible cycle at a much higher ask.',
        sourceIds: ['S43'],
      },
      {
        date: '2023-08-30',
        status: 'price_cut',
        ask: '$3,300',
        note: 'Cut before eventual removal.',
        sourceIds: ['S43'],
      },
      {
        date: '2023-09-09',
        status: 'off_market',
        ask: '$3,000',
        note: 'Redfin/Listanza-style context suggests final movement lower before disappearance.',
        sourceIds: ['S43'],
      },
    ],
    sourceIds: ['S43'],
  },
  {
    id: 'comp-61-building',
    label: '61 Roque Moraes Dr',
    address: '61 Roque Moraes Dr, Mill Valley',
    bedBath: '2/1',
    sqft: '855',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Same-street small-building context',
    features:
      'Deck, garage, storage, shared laundry in the same hillside cluster.',
    adjustment:
      'Useful same-street pattern match for owner-operated stock, though still a 2BR context comp.',
    currentAsk: '$2,950',
    firstSeenAt: '2019-07-07',
    lastSeenAt: '2019-11-01',
    daysVisible: 'Two short historical cycles',
    outcomeStatus: 'historical',
    outcomeConfidence: 'low',
    outcomeLabel: 'Historical removed listing',
    lifecycle: [
      {
        date: '2019-07-07',
        status: 'active',
        ask: '$2,800',
        note: 'Initial listing.',
        sourceIds: ['S44'],
      },
      {
        date: '2019-07-16',
        status: 'removed',
        ask: '$2,800',
        note: 'First cycle removed.',
        sourceIds: ['S44'],
      },
      {
        date: '2019-10-23',
        status: 'relisted',
        ask: '$2,950',
        note: 'Second visible cycle at a higher ask.',
        sourceIds: ['S44'],
      },
      {
        date: '2019-11-01',
        status: 'off_market',
        ask: '$2,950',
        note: 'Removed from the public feed.',
        sourceIds: ['S44'],
      },
    ],
    sourceIds: ['S44'],
  },
  {
    id: 'comp-71-2',
    label: '71 Roque Moraes Dr Apt 2',
    address: '71 Roque Moraes Dr Apt 2, Mill Valley',
    bedBath: '2/1',
    sqft: '920',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Active same-street 2BR calibration',
    features:
      'Renovated, back deck, shared laundry, storage locker, two covered spaces.',
    adjustment:
      'Current same-street 2BR ask that helps anchor the present internal 1BR-to-2BR spread.',
    currentAsk: '$3,095',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$3,095',
        note: 'Current same-street calibration listing.',
        sourceIds: ['S31'],
      },
    ],
    sourceIds: ['S31'],
  },
  {
    id: 'comp-1-shelley-b',
    label: '1 Shelley Dr Apt B',
    address: '1 Shelley Dr Apt B, Mill Valley',
    bedBath: '2/1',
    sqft: 'Not surfaced',
    observedAt: '2026-04-22',
    role: 'calibration',
    fit: 'Small-building 2BR ladder check',
    features:
      'Newly remodeled, shared laundry, off-street parking, storage.',
    adjustment:
      'Useful for the small-building 1BR-to-2BR ladder in the same immediate pocket.',
    currentAsk: '$2,950',
    firstSeenAt: '2026-04-22',
    outcomeStatus: 'active',
    outcomeConfidence: 'high',
    outcomeLabel: 'Active',
    lifecycle: [
      {
        date: '2026-04-22',
        status: 'active',
        ask: '$2,950',
        note: 'Current active 2BR calibration.',
        sourceIds: ['S32'],
      },
    ],
    sourceIds: ['S32'],
  },
];

const summarizeLatestAsk = (comp: ComparableRecord) =>
  comp.currentAsk ?? comp.lifecycle[comp.lifecycle.length - 1]?.ask ?? 'Not surfaced';

export const activeComps: CompRecord[] = comparables
  .filter((comp) => comp.outcomeStatus === 'active' && comp.bedBath === '1/1')
  .map((comp) => ({
    address: comp.address,
    ask: summarizeLatestAsk(comp),
    bedBath: comp.bedBath,
    sqft: comp.sqft,
    observed: comp.observedAt,
    fit: comp.fit,
    features: comp.features,
    adjustment: comp.adjustment,
    sourceIds: comp.sourceIds,
  }));

export const leasedOffMarketComps: ComparableRecord[] = comparables.filter(
  (comp) =>
    comp.outcomeStatus === 'off_market_inferred' ||
    comp.outcomeStatus === 'leased_confirmed'
);

export const calibrationComps: CompRecord[] = comparables
  .filter((comp) => comp.role === 'calibration')
  .map((comp) => ({
    address: comp.address,
    ask: summarizeLatestAsk(comp),
    bedBath: comp.bedBath,
    sqft: comp.sqft,
    observed: comp.observedAt,
    fit: comp.fit,
    features: comp.features,
    adjustment: comp.adjustment,
    sourceIds: comp.sourceIds,
  }));

export const historicalComps: HistoricalComp[] = [
  {
    name: '17 Roque Moraes Ct #1',
    observedFacts:
      'Listed 2025-01-24 at $2,675, removed 2025-02-28, relisted 2025-03-12 at $2,575, removed 2025-03-25.',
    timing: '35 days on first cycle, then 13 days on second cycle after a 3.7% cut.',
    readThrough:
      'Strongest exact-unit signal. The visible public market did not clear instantly at $2,675 but moved faster after the cut to $2,575.',
    sourceIds: ['S1', 'S2', 'S3'],
  },
  {
    name: '25 Roque Moraes Ct Apt 6',
    observedFacts:
      'Listed 2024-07-04 at $2,700, cut 2024-08-05 to $2,600, removed 2024-09-14, relisted 2024-11-14 at $2,600, removed 2024-11-24.',
    timing: '72-day first cycle, then a faster second cycle at the lower price.',
    readThrough:
      'Best same-street 1BR analog. Market resistance showed up at $2,700 and improved at $2,600.',
    sourceIds: ['S24'],
  },
  {
    name: '17 Shelley Dr #6',
    observedFacts:
      'Listed 2024-03-13 at $2,350, cut to $2,150 on 2024-03-20, cut to $1,995 on 2024-04-20, removed 2024-05-14.',
    timing: 'About 62 days visible with two cuts.',
    readThrough:
      'Useful walk-up floor for nearby 1BR product, but clearly weaker than Unit 1 on package.',
    sourceIds: ['S33'],
  },
  {
    name: '150 Seminary Dr Apt 2A',
    observedFacts:
      'Visible at $2,995 in 2020 and again in 2022 before removal in each cycle.',
    timing: 'Repeated short cycles at the same condo-grade ask.',
    readThrough:
      'Condo-grade ceiling comp showing a stronger package can support around $2,995, but it is not directly comparable to older small-building stock.',
    sourceIds: ['S36'],
  },
  {
    name: '160 Seminary Dr Apt 1-F',
    observedFacts:
      'Listed 2024-04-24 at $3,200, cut to $3,100 then $2,995, removed 2024-05-26; relisted 2025-12-05 at $3,100 and removed 2025-12-08.',
    timing: 'About 32 days in the main cycle with two cuts plus a later short cycle.',
    readThrough:
      'Strong overpricing ceiling signal. Even premium condo product needed to come down before disappearing.',
    sourceIds: ['S37'],
  },
  {
    name: '17 Roque Moraes building sale cycle',
    observedFacts:
      'Sale marketing started at $2.9M in September 2025, cut to $2.75M in October, and closed at $2.6M in February 2026.',
    timing: 'Roughly 135 days from list to close.',
    readThrough:
      'Not a rental comp, but a useful clearing-price proxy that undercuts purely aspirational upside stories.',
    sourceIds: ['S9', 'S10', 'S11', 'S14'],
  },
];

export const contradictions: ContradictionRecord[] = [
  {
    field: 'Stories',
    values:
      'Apartments.com and Redfin public record show 2 stories; Zillow, LoopNet, Redfin property details, and the microsite show 3.',
    resolution:
      'Display as mixed evidence and note that the disagreement likely turns on whether the parking level counts as a story.',
    severity: 'High',
    sourceIds: ['S9', 'S10', 'S12', 'S13', 'S17'],
  },
  {
    field: 'Lot size',
    values:
      '8,864 or 8,865 sqft on major portals; 9,697 sqft in one Compass public-record block; 10,155 sqft on Crexi.',
    resolution:
      'Use 8,864-8,865 sqft as the best-supported public figure and flag the outliers as unresolved.',
    severity: 'High',
    sourceIds: ['S9', 'S10', 'S11', 'S16'],
  },
  {
    field: '2010 sale price',
    values:
      '$1,150,000 on MLS-style close pages vs $1,106,000 in public-record style displays.',
    resolution:
      'Treat as MLS close versus recorded consideration and show both values where the 2010 sale is discussed.',
    severity: 'Medium',
    sourceIds: ['S10', 'S11', 'S14'],
  },
  {
    field: 'Late-2025 status sequence',
    values:
      'Portals disagree on the exact pending, removed, expired, and relisted dates in December 2025.',
    resolution:
      'Keep the sequence grouped and describe the disagreement instead of forcing one canonical date path.',
    severity: 'Medium',
    sourceIds: ['S9', 'S10', 'S11', 'S14'],
  },
  {
    field: 'Unit 4 off-market date',
    values: 'Zillow shows 2024-11-24; HotPads shows 2024-12-01.',
    resolution: 'Display both and note likely syndication lag.',
    severity: 'Low',
    sourceIds: ['S7', 'S8'],
  },
  {
    field: 'Parking wording',
    values:
      'Covered parking, carports, off-street parking, and attached garage language all appear.',
    resolution:
      'Normalize to parking benefit present, but keep exact wording in the source drawer.',
    severity: 'Medium',
    sourceIds: ['S9', 'S11', 'S14', 'S15', 'S17'],
  },
];

export const constraints: ConstraintRecord[] = [
  {
    title: 'California rent cap context',
    takeaway:
      'California Civil Code 1947.12 generally caps annual increases at 5% plus CPI with a 10% ceiling, based on the prior 12 months of gross rent.',
    applicability:
      'This older six-unit building looks more like likely AB 1482 territory than an obvious exemption case, but the dashboard should label this as public-source context, not legal advice.',
    sourceIds: ['S26', 'S9', 'S10'],
  },
  {
    title: 'Just-cause context',
    takeaway:
      'Civil Code 1946.2 brings just-cause protections into play after the statutory occupancy period, which matters when pricing for long-stay tenants.',
    applicability:
      'Useful to frame why vacancy strategy should not prioritize a weak tenant just to hit the highest face rent.',
    sourceIds: ['S27'],
  },
  {
    title: 'Mill Valley local overlay',
    takeaway:
      'Mill Valley public housing materials point to AB 1482 education and do not show an active city rent-stabilization ordinance for this property type.',
    applicability:
      'The pricing memo should frame the local overlay as limited and state-law-driven unless a parcel-specific legal review says otherwise.',
    sourceIds: ['S28'],
  },
  {
    title: 'Operational readiness',
    takeaway:
      'Marin County handles multi-unit inspection oversight for rental health and safety, which matters if you try to price at the upper end of the band.',
    applicability:
      'Upper-band pricing only works if the building and unit are truly rent-ready.',
    sourceIds: ['S29'],
  },
  {
    title: 'Optional program path',
    takeaway:
      'Mill Valley has a Lease to Locals pilot with its own pricing and eligibility limits.',
    applicability:
      'Not core to the Unit 1 recommendation, but worth surfacing as an optional branch if the target rent stays below the program ceiling.',
    sourceIds: ['S30'],
  },
];

export const evidenceRecords: EvidenceRecord[] = [
  {
    id: 'E1',
    subject: 'Unit 1',
    category: 'Observed rent history',
    claim:
      'Unit 1 visibly priced at $2,675 before cutting to $2,575 in March 2025.',
    sourceIds: ['S1', 'S2', 'S3'],
  },
  {
    id: 'E2',
    subject: 'Unit 2',
    category: 'Observed rent history',
    claim:
      'Unit 2 also needed a modest cut before disappearing from the market in March 2025.',
    sourceIds: ['S4', 'S5', 'S6'],
  },
  {
    id: 'E3',
    subject: 'Unit 4',
    category: 'Observed rent history',
    claim:
      'Unit 4, a 2BR, surfaced at $3,000 in November 2024 and came off market within roughly a month.',
    sourceIds: ['S7', 'S8'],
  },
  {
    id: 'E4',
    subject: 'Building',
    category: 'Public facts',
    claim:
      'The property is consistently represented as a six-unit, 1958 multifamily building with views, parking, storage, and common laundry.',
    sourceIds: ['S9', 'S10', 'S12', 'S14', 'S17'],
  },
  {
    id: 'E5',
    subject: 'Unit 3 / 5 / 6',
    category: 'Trace gaps',
    claim:
      'No exact public pages were found for Units 3, 5, or 6 in this research pass, and the site should show that explicitly.',
    sourceIds: ['S9', 'S10', 'S11', 'S12', 'S13', 'S14', 'S15'],
  },
  {
    id: 'E6',
    subject: 'Active comps',
    category: 'Current market',
    claim:
      'The most relevant active 1BR comps currently run from the low $2,000s into the high $2,000s, with $3,000-plus reserved for stronger packages.',
    sourceIds: ['S18', 'S20', 'S21', 'S22', 'S23'],
  },
  {
    id: 'E7',
    subject: 'Leased / off-market comps',
    category: 'Clearing-price proxy',
    claim:
      'Same-unit, same-street, and nearby removed-listing evidence clusters around the mid-$2,500s to mid-$2,600s for the strongest Roque Moraes-style 1BR analogs.',
    sourceIds: ['S1', 'S3', 'S24', 'S33', 'S36', 'S37'],
  },
  {
    id: 'E8',
    subject: 'Constraints',
    category: 'Legal context',
    claim:
      'The recommendation should present AB 1482 and just-cause rules as public-source context and not as legal advice.',
    sourceIds: ['S26', 'S27', 'S28'],
  },
  {
    id: 'E9',
    subject: '2BR calibration',
    category: 'Internal rent ladder',
    claim:
      'Same-building and same-street 2BR traces show a fairly tight 1BR-to-2BR spread in this pocket.',
    sourceIds: ['S4', 'S7', 'S39', 'S45', 'S47'],
  },
];
